import React, { useState, useEffect, useRef } from 'react';

const API_URL = '';

// ===== Consultation Bookings Panel =====
export const ConsultationBookingsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/consultation-bookings`);
      setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-gray-400" data-testid="consult-panel-loading">Loading...</div>;
  const bookings = (data && data.bookings) || [];

  return (
    <div className="bg-white rounded-xl shadow p-4" data-testid="consultation-bookings-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h3 className="text-lg font-bold text-gray-800">📞 Consultation Bookings</h3>
        <span className="text-xs text-gray-500">कुल: <b>{data?.total || 0}</b> · <b className="text-green-600">{data?.paid || 0} paid</b> · <b className="text-amber-600">{data?.pending || 0} pending</b></span>
        <button onClick={load} className="ml-auto px-3 py-1 text-xs font-bold bg-blue-600 text-white rounded-lg" data-testid="consult-refresh-btn">↻ Refresh</button>
      </div>
      {bookings.length === 0 ? (
        <div className="text-center py-8 text-gray-400">अभी कोई consultation booking नहीं</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 uppercase text-xs border-b">
              <th className="p-2">नाम</th><th className="p-2">Mobile</th><th className="p-2">विषय</th><th className="p-2">पसंदीदा समय</th><th className="p-2">कब</th><th className="p-2">Payment</th>
            </tr></thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-gray-100" data-testid={`consult-row-${b.id}`}>
                  <td className="p-2 font-semibold text-gray-800">{b.name}</td>
                  <td className="p-2"><a href={`tel:+91${b.mobile1}`} className="text-blue-600">{b.mobile1}</a></td>
                  <td className="p-2 text-gray-600 max-w-xs">{b.consultation_topic || '—'}</td>
                  <td className="p-2 text-gray-600 whitespace-nowrap">{b.consultation_preferred_time || 'कोई भी'}</td>
                  <td className="p-2 text-gray-400 whitespace-nowrap text-xs">{b.consultation_requested_at ? new Date(b.consultation_requested_at).toLocaleString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td className="p-2">{b.payment_status === 'paid'
                    ? <span className="text-green-600 font-bold">✓ ₹{b.payment_amount || 2100}</span>
                    : <span className="text-amber-600 font-bold">⏳ Pending</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ===== WhatsApp API Chat Panel (Meta Cloud API) =====
export const WaApiChatPanel = () => {
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const loadChats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/wa-api-chats`);
      const d = await res.json();
      setChats(d.chats || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const openThread = async (phone) => {
    setActive(phone);
    try {
      const res = await fetch(`${API_URL}/api/admin/wa-api-chat/${phone}`);
      setThread(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadChats();
    pollRef.current = setInterval(() => { loadChats(); if (active) openThread(active); }, 15000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [active]);

  const typeLabel = (t) => ({
    confirm_template: '📋 Confirmation भेजी',
    report_template: '📄 Report भेजी',
    auto_reply: '🤖 Auto-reply',
    text: '💬',
  }[t] || t);

  return (
    <div className="bg-white rounded-xl shadow p-4" data-testid="wa-api-chat-panel">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-lg font-bold text-gray-800">📲 WhatsApp API Chats <span className="text-green-600 text-xs font-bold animate-pulse">● LIVE</span></h3>
        <span className="text-xs text-gray-500">(Meta Cloud API — customer के messages + भेजे गए confirmation/report)</span>
        <button onClick={loadChats} className="ml-auto px-3 py-1 text-xs font-bold bg-blue-600 text-white rounded-lg" data-testid="wa-api-refresh-btn">↻ Refresh</button>
      </div>
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Chat list */}
          <div className="md:col-span-1 border border-gray-100 rounded-lg max-h-[520px] overflow-y-auto">
            {chats.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">अभी कोई WhatsApp chat नहीं</div>
            ) : chats.map((c) => (
              <button key={c.phone} onClick={() => openThread(c.phone)}
                className={`w-full text-left p-3 border-b border-gray-100 hover:bg-green-50 ${active === c.phone ? 'bg-green-50' : ''}`}
                data-testid={`wa-api-chat-item-${c.phone}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-800 text-sm">{c.name || c.phone}</span>
                  {c.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5">{c.unread}</span>}
                </div>
                <div className="text-xs text-gray-500 truncate">{c.last_dir === 'in' ? '↩ ' : '↪ '}{c.last_text}</div>
                <div className="text-[10px] text-gray-400">{c.last_at ? new Date(c.last_at).toLocaleString('hi-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</div>
              </button>
            ))}
          </div>
          {/* Thread */}
          <div className="md:col-span-2 border border-gray-100 rounded-lg p-3 max-h-[520px] overflow-y-auto bg-[#ece5dd]" data-testid="wa-api-thread">
            {!thread ? (
              <div className="text-center py-20 text-gray-400 text-sm">बायीं ओर से कोई chat चुनें</div>
            ) : (thread.messages || []).length === 0 ? (
              <div className="text-center py-20 text-gray-400 text-sm">कोई message नहीं</div>
            ) : (
              <div className="space-y-2">
                <div className="text-center text-xs text-gray-500 mb-2">+91 {thread.phone} {thread.name ? `· ${thread.name}` : ''}</div>
                {(thread.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.dir === 'in' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow ${m.dir === 'in' ? 'bg-white text-gray-800' : 'bg-[#dcf8c6] text-gray-800'}`}>
                      {m.dir === 'out' && <div className="text-[10px] font-bold text-green-700 mb-0.5">{typeLabel(m.type)}{m.status === 'failed' && <span className="text-red-500"> ✗ नहीं गई</span>}{m.status === 'sent' && <span className="text-green-600"> ✓</span>}</div>}
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      <div className="text-[10px] text-gray-400 text-right mt-0.5">{m.at ? new Date(m.at).toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
